import "./platform.css";

import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";

import type {
  PanelPlatformAuditEntry,
  PanelPlatformAuditResponse,
  PanelPlatformChannelOverview,
  PanelPlatformMembersResponse,
  PanelMember,
  PanelTwitchUser,
} from "../panel-contract";
import { CHANNEL_ROLES } from "../contracts/values";
import {
  changePlatformMember,
  removePlatformMember,
  addPlatformMember,
  releasePlatformChannel,
  getPlatformAudit,
  getPlatformMembers,
  getPlatformOverview,
  PanelApiError,
  setPlatformFullConsent,
  searchPlatformUser,
} from "./api";
import { platformActionLabel, platformTexts, roleLabel } from "./labels";
import { apiErrorText, dashboardCommonTexts, formatTimestamp, formatNumber } from "./locale";
import { Badge, Button, ConfirmDialog, Field, FormDialog, InspectorActions, InspectorFieldRow, InspectorHeading, InspectorSection, ListDetail, ListPaginationFooter, LoadState as LoadStateView, LoadStateLines, notify, PageHeader, Select, SubInspector, SwitchRow, Tabs, useInspectorSelection, type SelectOption } from "./ui";
import { MemberGrantEditor } from "./member-grant-editor";
import { StateRow, type StateTone } from "./module-panels";

interface PlatformPageProperties {
  onAuthenticationRequired: () => void;
}

interface LoadState<T> {
  status: "idle" | "loading" | "success" | "error";
  data: T | null;
  error: string | null;
}

const emptyLoadState = <T,>(): LoadState<T> => ({ status: "idle", data: null, error: null });
const loadState = <T,>(): LoadState<T> => ({ status: "loading", data: null, error: null });
const loadedState = <T,>(data: T): LoadState<T> => ({ status: "success", data, error: null });

const errorText = (error: unknown, fallback: string): string =>
  error instanceof PanelApiError ? apiErrorText(error.code, fallback) : fallback;

const isAbort = (error: unknown): boolean =>
  error instanceof DOMException && error.name === "AbortError";

const memberName = (member: PanelMember): string =>
  member.displayName ?? (member.login === null ? platformTexts().member : "@" + member.login);

const roleSelectOptions = (): SelectOption[] => CHANNEL_ROLES.filter((role) => role !== "broadcaster").map((role) => ({
  value: role,
  label: roleLabel(role),
}));

const connectionTone = (channel: PanelPlatformChannelOverview): StateTone =>
  channel.broadcasterConnected ? "healthy" : channel.fullConsent ? "warning" : "neutral";

const connectionWord = (channel: PanelPlatformChannelOverview, compact = false): string => {
  const texts = platformTexts();
  if (channel.broadcasterConnected) return texts.connected;
  if (!channel.fullConsent) return texts.no;
  return compact ? texts.consentPendingShort : texts.consentPending;
};

const MembersTable = ({
  members: members,
  selectedUserId,
  onSelect: onSelect,
}: {
  members: PanelMember[];
  selectedUserId: string | null;
  onSelect: (member: PanelMember) => void;
}): ReactElement => {
  const texts = platformTexts();
  if (members.length === 0) return <p className="muted">{texts.noMembers}</p>;
  return (
    <div className="table-wrap">
      <table className="table platform-members-table">
        <thead>
          <tr>
            <th scope="col">{texts.login}</th>
            <th scope="col">{texts.role}</th>
          </tr>
        </thead>
        <tbody>
          {members.map((member) => (
            <tr key={member.userId} tabIndex={0} aria-selected={selectedUserId === member.userId} onClick={() => { onSelect(member); }} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onSelect(member); } }}>
              <th scope="row">
                <span>{memberName(member)}</span>
                <span className="login-hint" title={texts.twitchId(member.userId)}>{member.login === null ? texts.member : "@" + member.login}</span>
              </th>
              <td><Badge tone={member.role === "broadcaster" ? "brand" : "neutral"}>{roleLabel(member.role)}</Badge></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PlatformMembersLoading = (): ReactElement => {
  const texts = platformTexts();
  return (
    <><LoadStateLines rows={100} height={44} /><span className="sr-only">{texts.loadMembers}</span></>
  );
};

const ChannelInspector = ({
  channel: channel,
  onAuthenticationRequired: onAuthenticationRequired,
  onReloadOverview: onReload,
  onClose,
}: {
  channel: PanelPlatformChannelOverview;
  onAuthenticationRequired: () => void;
  onReloadOverview: () => Promise<void>;
  onClose: () => void;
}): ReactElement => {
  const texts = platformTexts();
  const common = dashboardCommonTexts();
  const [members, setMembers] = useState<LoadState<PanelPlatformMembersResponse>>(() => emptyLoadState());
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<PanelMember | null>(null);
  const [consentInProgress, setConsentInProgress] = useState(false);
  const notifiedMembersError = useRef<string | null>(null);

  const loadMembers = async (): Promise<void> => {
    setMembers((current) => current.data === null ? loadState() : { ...current, status: "loading", error: null });
    try {
      setMembers(loadedState(await getPlatformMembers(channel.channelId)));
    } catch (error: unknown) {
      if (error instanceof PanelApiError && error.status === 401) onAuthenticationRequired();
      if (!isAbort(error)) setMembers({ status: "error", data: null, error: errorText(error, texts.error) });
    }
  };

  useEffect(() => {
    let aborted = false;
    const load = async (): Promise<void> => {
      try {
        const data = await getPlatformMembers(channel.channelId);
        if (!aborted) setMembers(loadedState(data));
      } catch (error: unknown) {
        if (!aborted && !isAbort(error)) {
          setMembers({ status: "error", data: null, error: errorText(error, texts.error) });
          if (error instanceof PanelApiError && error.status === 401) onAuthenticationRequired();
        }
      }
    };
    void load();
    return () => { aborted = true; };
  }, [channel.channelId, onAuthenticationRequired, texts.error]);

  useEffect(() => {
    if (members.error === null) {
      notifiedMembersError.current = null;
      return;
    }
    if (notifiedMembersError.current === members.error) return;
    notifiedMembersError.current = members.error;
    notify({ tone: "error", message: members.error });
  }, [members.error]);

  const toggleConsent = async (): Promise<void> => {
    setConsentInProgress(true);
    try {
      await setPlatformFullConsent(channel.channelId, !channel.fullConsent);
      await onReload();
    } catch (error: unknown) {
      notify({ tone: "error", message: errorText(error, texts.error) });
      if (error instanceof PanelApiError && error.status === 401) onAuthenticationRequired();
    } finally {
      setConsentInProgress(false);
    }
  };

  const changeRole = async (member: PanelMember, role: "manager" | "operator"): Promise<void> => {
    setBusyUserId(member.userId);
    try {
      await changePlatformMember(channel.channelId, member.userId, role);
      await loadMembers();
    } catch (error: unknown) {
      notify({ tone: "error", message: errorText(error, texts.error) });
      if (error instanceof PanelApiError && error.status === 401) onAuthenticationRequired();
    } finally {
      setBusyUserId(null);
    }
  };

  const selectedMember = members.data?.members.find((member) => member.userId === selectedMemberId) ?? null;

  const removeMember = async (member: PanelMember): Promise<void> => {
    setBusyUserId(member.userId);
    try {
      await removePlatformMember(channel.channelId, member.userId);
      setPendingRemoval(null);
      await loadMembers();
    } catch (error: unknown) {
      notify({ tone: "error", message: errorText(error, texts.error) });
      if (error instanceof PanelApiError && error.status === 401) onAuthenticationRequired();
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <SubInspector ariaLabel={texts.editChannel(channel.displayName)} title={texts.editChannel(channel.displayName)} identifier={channel.channelId} closeLabel={common.close} onClose={onClose}>
      <StateRow
        label={texts.identity}
        tone={connectionTone(channel)}
        word={connectionWord(channel)}
        detail={!channel.broadcasterConnected && channel.fullConsent ? texts.consentPendingHint : channel.login}
      />
      <section className="config-section platform-inspector-section" aria-label={texts.toggleConsent}>
        <SwitchRow
          label={texts.fullConsent}
          hint={common.immediate}
          checked={channel.fullConsent}
          pending={consentInProgress}
          onChange={() => { void toggleConsent(); }}
        />
      </section>
      <InvitationLink channel={channel} />
      <section className="config-section platform-inspector-section" aria-label={texts.addMember}>
        <div className="section-heading"><h3>{texts.addMember}</h3></div>
        <MemberGrantEditor
          roles={["manager", "operator"]}
          defaultRole="operator"
          texts={{
            ariaLabel: texts.addMember,
            title: texts.addMember,
            searchLabel: texts.twitchLogin,
            searchHint: texts.twitchLoginHint,
            searchButton: texts.search,
            searchingButton: texts.searching,
            roleLabel: texts.role,
            roleHint: texts.roleHint,
            saveLabel: texts.add,
            confirmTitle: texts.addMemberConfirmTitle,
            confirmDescription: texts.addMemberConfirmDescription,
            confirmButton: texts.addMemberConfirmButton,
          }}
          onSearch={(login) => searchPlatformUser(login).then((response) => response.user)}
          onGrant={(userId, role) => addPlatformMember(channel.channelId, userId, role as "manager" | "operator").then(() => undefined)}
          errorText={(error) => errorText(error, texts.error)}
          onGranted={() => { void loadMembers(); }}
          onAuthenticationRequired={onAuthenticationRequired}
        />
      </section>
      {selectedMember === null ? null : <>
        <InspectorSection title={roleLabel(selectedMember.role)}>
          {selectedMember.role === "broadcaster" ? <p className="muted" id={`platform-remove-broadcaster-reason-${selectedMember.userId}`}>{texts.removeBroadcasterHint}</p> : <InspectorFieldRow label={texts.role}>
            <Select ariaLabel={texts.role + ": " + memberName(selectedMember)} value={selectedMember.role}
              disabled={busyUserId === selectedMember.userId}
              onChange={(role) => { if (role !== null) void changeRole(selectedMember, role as "manager" | "operator"); }}
              options={roleSelectOptions()} />
          </InspectorFieldRow>}
        </InspectorSection>
        <InspectorActions destructive={
          <Button danger="subtle" disabled={selectedMember.role === "broadcaster" || busyUserId === selectedMember.userId}
            {...(selectedMember.role === "broadcaster" ? { title: texts.removeBroadcasterHint } : {})}
            {...(selectedMember.role === "broadcaster" ? { describedBy: `platform-remove-broadcaster-reason-${selectedMember.userId}` } : {})}
            onClick={() => { setPendingRemoval(selectedMember); }}>{texts.remove}</Button>
        } />
      </>}
      <InspectorSection title={texts.members}>
        <LoadStateView
          variant="panel-220"
          status={members.data === null ? members.status === "error" ? "error" : "loading" : members.data.members.length === 0 ? "empty" : "success"}
          loading={<PlatformMembersLoading />}
          empty={<MembersTable members={[]} selectedUserId={selectedMemberId} onSelect={(member) => { setSelectedMemberId(member.userId); }} />}
          error={<PlatformMembersLoading />}
      >{members.data === null || members.data.members.length === 0 ? null : <MembersTable
            members={members.data.members}
            selectedUserId={selectedMemberId}
            onSelect={(member) => { setSelectedMemberId(member.userId); }}
          />}</LoadStateView>
      </InspectorSection>
      <ConfirmDialog
        opened={pendingRemoval !== null}
        title={texts.removeConfirmTitle(pendingRemoval === null ? "" : memberName(pendingRemoval))}
        description={texts.removeQuestion(pendingRemoval === null ? "" : memberName(pendingRemoval))}
        confirmLabel={texts.confirmRemove(pendingRemoval === null ? "" : memberName(pendingRemoval))}
        cancelLabel={common.cancel}
        danger
        onCancel={() => { setPendingRemoval(null); }}
        onConfirm={() => { if (pendingRemoval !== null) void removeMember(pendingRemoval); }}
      />
    </SubInspector>
  );
};

const ChannelRelease = ({
  opened,
  onReloadOverview: onReload,
  onAuthenticationRequired: onAuthenticationRequired,
  onClose,
}: {
  opened: boolean;
  onReloadOverview: () => Promise<void>;
  onAuthenticationRequired: () => void;
  onClose: () => void;
}): ReactElement => {
  const texts = platformTexts();
  const common = dashboardCommonTexts();
  const [login, setLogin] = useState("");
  const [searchInProgress, setSearchInProgress] = useState(false);
  const [searchError, setSearchError] = useState<string | undefined>(undefined);
  const [found, setFound] = useState<PanelTwitchUser | null>(null);
  const [fullConsent, setFullConsent] = useState(true);
  const [confirmation, setConfirmation] = useState(false);
  const [releaseInProgress, setReleaseInProgress] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const close = (): void => {
    if (searchInProgress || releaseInProgress) return;
    setConfirmation(false);
    setSearchError(undefined);
    setActionError(undefined);
    setFound(null);
    setLogin("");
    onClose();
  };

  const searchUser = async (): Promise<void> => {
    const trimmed = login.trim();
    if (searchInProgress || trimmed.length === 0) return;
    setSearchInProgress(true);
    setSearchError(undefined);
    setFound(null);
    try {
      setFound((await searchPlatformUser(trimmed)).user);
      setFullConsent(true);
    } catch (error: unknown) {
      setSearchError(errorText(error, texts.error));
      if (error instanceof PanelApiError && error.status === 401) onAuthenticationRequired();
    } finally {
      setSearchInProgress(false);
    }
  };

  const releaseChannel = async (): Promise<void> => {
    if (found === null) return;
    setReleaseInProgress(true);
    setActionError(undefined);
    try {
      await releasePlatformChannel(found.login, fullConsent);
      setConfirmation(false);
      setFound(null);
      setLogin("");
      await onReload();
      notify({ tone: "success", message: common.saved });
      onClose();
    } catch (error: unknown) {
      setConfirmation(false);
      setActionError(errorText(error, texts.error));
      if (error instanceof PanelApiError && error.status === 401) onAuthenticationRequired();
    } finally {
      setReleaseInProgress(false);
    }
  };

  return (
    <>
      <FormDialog
        opened={opened}
        title={texts.releaseChannel}
        pending={searchInProgress || releaseInProgress}
        confirmDisabled={found === null}
        {...(actionError === undefined ? {} : { error: actionError })}
        onConfirm={() => { if (found !== null) setConfirmation(true); }}
        onCancel={close}
        confirmLabel={texts.releaseChannel}
        cancelLabel={common.cancel}
      >
        <div className="form-row platform-channel-release__search">
          <Field label={texts.twitchLogin} hint={texts.twitchLoginHint} prefix="@" value={login} onChange={(value) => { setLogin(value); setSearchError(undefined); }} {...(searchError === undefined ? {} : { error: searchError })} disabled={searchInProgress || releaseInProgress} onKeyDown={(event) => { if (event.key !== "Enter") return; event.preventDefault(); void searchUser(); }} />
          <Button className="platform-channel-release__search-button" variant="neutral" disabled={searchInProgress || releaseInProgress || login.trim().length === 0} onClick={() => { void searchUser(); }}>{searchInProgress ? texts.searching : texts.search}</Button>
        </div>
        <div className="platform-channel-release__result-slot">
          {found === null ? null : (
            <div className="member-grant-editor__result">
              <div><strong>{texts.userFound}: {found.displayName}</strong><span>@{found.login} · {texts.twitchId(found.userId)}</span></div>
              <SwitchRow label={texts.fullConsent} description={texts.fullConsentCardDescription} checked={fullConsent} disabled={releaseInProgress} onChange={setFullConsent} />
            </div>
          )}
        </div>
      </FormDialog>
      {found === null ? null : (
        <ConfirmDialog
          opened={confirmation}
          title={texts.releaseChannelQuestion(found.displayName)}
          description={texts.releaseChannelDescription(found.displayName, found.userId, fullConsent ? texts.yes : texts.no)}
          confirmLabel={texts.confirmRelease}
          cancelLabel={common.cancel}
          onCancel={() => { setConfirmation(false); }}
          onConfirm={() => { void releaseChannel(); }}
          pending={releaseInProgress}
        />
      )}
    </>
  );
};

const InvitationLink = ({ channel: channel }: { channel: PanelPlatformChannelOverview }): ReactElement => {
  const texts = platformTexts();
  const [copied, setCopied] = useState(false);
  const link = window.location.origin + "/auth/login?channel=" + encodeURIComponent(channel.login);

  const copyLinkToClipboard = async (): Promise<void> => {
    setCopied(false);
    const clipboard = Reflect.get(navigator, "clipboard") as { writeText: (text: string) => Promise<void> } | undefined;
    if (clipboard === undefined) return;
    await clipboard.writeText(link);
    setCopied(true);
  };

  return (
    <section className="config-section platform-inspector-section" aria-label={texts.invitationLink}>
      <div className="section-heading"><h2>{texts.invitationLink}</h2></div>
      <div className="form-row">
        <Field label={texts.invitationLink} hint={texts.invitationLinkHint} value={link} onChange={() => {}} readOnly mono />
        <Button variant="neutral" icon={copied ? "copied" : "copy"} onClick={() => { void copyLinkToClipboard(); }}>{copied ? texts.linkCopied : texts.copyLink}</Button>
      </div>
      {!channel.broadcasterConnected && channel.fullConsent ? <StateRow label={texts.identity} tone="warning" word={texts.consentPending} detail={texts.consentPendingHint} /> : null}
    </section>
  );
};

const PlatformAudit = ({
  auditState: auditState,
  channels: channels,
  onLoadMore: onLoadMore,
  onRetry: onRetry,
  loadingMore: loadingMore,
}: {
  auditState: LoadState<PanelPlatformAuditResponse>;
  channels: PanelPlatformChannelOverview[];
  onLoadMore: () => void;
  onRetry: () => void;
  loadingMore: boolean;
}): ReactElement => {
  const texts = platformTexts();
  const channelNames = useMemo(() => new Map(channels.map((channel) => [channel.channelId, channel.login])), [channels]);
  const actorName = (entry: PanelPlatformAuditEntry): string =>
    entry.actorDisplayName ?? (entry.actorLogin == null ? entry.actorUserId : `@${entry.actorLogin}`);
  const actor = (entry: PanelPlatformAuditEntry): string =>
    (entry.actorKind === "platform_admin" ? texts.platformAdmin : texts.member) + " · " + actorName(entry);
  const status = auditState.data === null
    ? auditState.status === "error" ? "error" : "loading"
    : auditState.data.entries.length === 0 ? "empty" : "success";
  return (
    <section className="config-section" aria-label={texts.audit}>
      <div className="section-heading"><h2>{texts.audit}</h2></div>
      <LoadStateView
        variant="panel-280"
        status={status}
        loading={<LoadStateLines rows={6} height={34} />}
        empty={<p className="muted">{texts.auditEmpty}</p>}
        error={<p role="alert">{auditState.error ?? texts.error}</p>}
        queryError={{ message: auditState.error ?? texts.error, onRetry }}
      >{auditState.data === null || auditState.data.entries.length === 0 ? null : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th scope="col">{texts.login}</th><th scope="col">{texts.action}</th><th scope="col">{texts.actor}</th><th scope="col">{texts.timestamp}</th></tr></thead>
            <tbody>{auditState.data.entries.map((entry) => <tr key={entry.auditId}><th scope="row">{channelNames.get(entry.channelId) ?? <span className="mono">{entry.channelId}</span>}</th><td>{platformActionLabel(entry.action)}</td><td className="mono">{actor(entry)}</td><td className="mono" title={entry.createdAt}>{formatTimestamp(entry.createdAt)}</td></tr>)}</tbody>
          </table>
        </div>
      )}</LoadStateView>
      {(auditState.data?.entries.length ?? 0) === 0 && auditState.data?.nextCursor == null ? null : <ListPaginationFooter loadedCount={auditState.data?.entries.length ?? 0} loadedLabel={texts.loaded}>
        {auditState.data?.nextCursor == null ? null : <button className="button button--secondary" type="button" onClick={onLoadMore} disabled={loadingMore}>{loadingMore ? texts.loadingMore : texts.loadMore}</button>}
      </ListPaginationFooter>}
    </section>
  );
};

export const PlatformPage = ({ onAuthenticationRequired: onAuthenticationRequired }: PlatformPageProperties): ReactElement => {
  const texts = platformTexts();
  const [overview, setOverview] = useState<LoadState<PanelPlatformChannelOverview[]>>(() => emptyLoadState());
  const [audit, setAudit] = useState<LoadState<PanelPlatformAuditResponse>>(() => emptyLoadState());
  const { selectedKey: selectedChannelId, select: selectChannel, rowRef: channelRowRef, close: closeChannel } = useInspectorSelection<string>();
  const [channelReleaseOpen, setChannelReleaseOpen] = useState(false);
  const channelReleaseButton = useRef<HTMLButtonElement | null>(null);
  const [auditLoadingMore, setAuditLoadingMore] = useState(false);
  const notifiedPageError = useRef<string | null>(null);
  // Initial-load errors render in place of the list; the toast only covers failures while data is already shown.
  const pageError = (overview.data === null ? null : overview.error) ?? (audit.data === null ? null : audit.error);

  useEffect(() => {
    if (pageError === null) {
      notifiedPageError.current = null;
      return;
    }
    if (notifiedPageError.current === pageError) return;
    notifiedPageError.current = pageError;
    notify({ tone: "error", message: pageError });
  }, [pageError]);

  const openChannelRelease = (): void => {
    closeChannel();
    setChannelReleaseOpen(true);
    channelReleaseButton.current?.focus();
  };

  const closeChannelRelease = (): void => {
    setChannelReleaseOpen(false);
    channelReleaseButton.current?.focus();
  };

  const closeFloating = useCallback((): void => {
    if (selectedChannelId !== null) closeChannel();
  }, [selectedChannelId, closeChannel]);

  // Every load (initial or retry) is guarded: a newer request or an unmount makes older results, including a 401, irrelevant.
  const overviewGeneration = useRef(0);
  const auditGeneration = useRef(0);
  const unmounted = useRef(false);
  useEffect(() => {
    unmounted.current = false;
    return () => { unmounted.current = true; };
  }, []);

  const loadOverview = async (): Promise<void> => {
    const generation = ++overviewGeneration.current;
    const stale = (): boolean => unmounted.current || generation !== overviewGeneration.current;
    setOverview((current) => current.data === null ? loadState() : { ...current, status: "loading", error: null });
    try {
      const response = await getPlatformOverview();
      if (stale()) return;
      setOverview(loadedState(response.channels));
      if (selectedChannelId !== null && !response.channels.some((channel) => channel.channelId === selectedChannelId)) closeChannel();
    } catch (error: unknown) {
      if (stale()) return;
      setOverview({ status: "error", data: null, error: errorText(error, texts.error) });
      if (error instanceof PanelApiError && error.status === 401) onAuthenticationRequired();
    }
  };

  const loadAudit = async (): Promise<void> => {
    const generation = ++auditGeneration.current;
    const stale = (): boolean => unmounted.current || generation !== auditGeneration.current;
    setAudit(loadState());
    try {
      const response = await getPlatformAudit();
      if (!stale()) setAudit(loadedState(response));
    } catch (error: unknown) {
      if (stale()) return;
      setAudit({ status: "error", data: null, error: errorText(error, texts.error) });
      if (error instanceof PanelApiError && error.status === 401) onAuthenticationRequired();
    }
  };

  // Overview and audit load independently so one failing request cannot block the other tab.
  const loaders = useRef({ loadOverview, loadAudit });
  loaders.current = { loadOverview, loadAudit };
  useEffect(() => { void loaders.current.loadOverview(); }, [onAuthenticationRequired]);
  useEffect(() => { void loaders.current.loadAudit(); }, [onAuthenticationRequired]);

  const selectedChannel = overview.data?.find((channel) => channel.channelId === selectedChannelId) ?? null;
  const inspectorOpen = selectedChannelId !== null;
  const overviewStatus = overview.data === null
    ? overview.status === "error" ? "error" : "loading"
    : overview.data.length === 0 ? "empty" : "success";

  const loadMoreAudit = async (): Promise<void> => {
    if (auditLoadingMore || audit.data?.nextCursor === null || audit.data?.nextCursor === undefined) return;
    setAuditLoadingMore(true);
    try {
      const nextPage = await getPlatformAudit(audit.data.nextCursor);
      setAudit((current) => current.data === null ? current : loadedState({ entries: current.data.entries.concat(nextPage.entries), nextCursor: nextPage.nextCursor }));
    } catch (error: unknown) {
      setAudit((current) => ({ ...current, status: "error", error: errorText(error, texts.error) }));
      if (error instanceof PanelApiError && error.status === 401) onAuthenticationRequired();
    } finally {
      setAuditLoadingMore(false);
    }
  };

  return (
    <section className="module-stack" aria-label={texts.title}>
      <PageHeader kind="platform" title={texts.title} subtitle={texts.subtitle(formatNumber(overview.data?.length ?? 0))} actions={
        <Button ref={channelReleaseButton} variant="primary" icon="add" onClick={openChannelRelease}>{texts.releaseChannel}</Button>
      } />
      <Tabs className="platform-tabs" defaultValue="channels" keepMounted={false}>
        <Tabs.List className="platform-tabs__list" aria-label={texts.title}>
          <Tabs.Tab className="platform-tabs__tab" value="channels">{texts.channels}</Tabs.Tab>
          <Tabs.Tab className="platform-tabs__tab" value="audit">{texts.auditTab}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel className="platform-tabs__panel" value="channels">
          <section className="config-section" aria-label={texts.channelOverview}>
            <ListDetail
              list={
                <div>
                  <InspectorHeading level="h2" title={texts.channelOverview} />
                  <LoadStateView
                    variant="panel-360"
                    status={overviewStatus}
                    loading={<LoadStateLines rows={6} height={34} />}
                    empty={<p className="muted">{texts.noChannels}</p>}
                    error={<p role="alert">{overview.error ?? texts.error}</p>}
                    queryError={{ message: overview.error ?? texts.error, onRetry: () => { void loadOverview(); } }}
                  >{overview.data === null || overview.data.length === 0 ? null : (
                    <div className="table-wrap">
                      <table className={`table table--content platform-channel-table${inspectorOpen ? " platform-channel-table--inspector-open" : ""}`}>
                        <thead><tr><th scope="col">{texts.login}</th>{inspectorOpen ? null : <th scope="col">{texts.identifier}</th>}<th scope="col" title={texts.fullConsent}>{texts.fullConsentColumn}</th><th scope="col" title={`${texts.broadcaster} · ${texts.manager} · ${texts.operator}`}>{texts.members}</th><th scope="col">{inspectorOpen ? texts.identityShort : texts.identity}</th></tr></thead>
                        <tbody>{overview.data.map((channel) => {
                          const roleCountsTitle = `${texts.broadcaster} · ${texts.manager} · ${texts.operator}`;
                          return <tr key={channel.channelId} ref={channelRowRef(channel.channelId)} tabIndex={0} aria-selected={channel.channelId === selectedChannelId} onClick={() => { setChannelReleaseOpen(false); selectChannel(channel.channelId); }} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setChannelReleaseOpen(false); selectChannel(channel.channelId); } }}>
                            <th scope="row" data-field="login" data-label={texts.login} title={inspectorOpen ? channel.channelId : channel.login}>{channel.login}</th>
                            {inspectorOpen ? null : <td className="mono" data-field="identifier" data-label={texts.identifier} title={channel.channelId}>{channel.channelId}</td>}
                            <td data-field="consent" data-label={texts.fullConsentColumn}>{channel.fullConsent ? texts.yes : texts.no}</td>
                            <td className="number" data-field="members" data-label={texts.members} title={roleCountsTitle}>{[channel.memberCounts.broadcaster, channel.memberCounts.manager, channel.memberCounts.operator].map((count) => formatNumber(count)).join(" · ")}</td>
                            <td data-field="identity" data-label={inspectorOpen ? texts.identityShort : texts.identity}><span className="led" data-status={connectionTone(channel) === "healthy" ? "green" : connectionTone(channel) === "warning" ? "amber" : "off"}><span className="led__dot" aria-hidden="true" /><span>{connectionWord(channel, inspectorOpen)}</span></span></td>
                          </tr>;
                        })}</tbody>
                      </table>
                    </div>
                  )}</LoadStateView>
                </div>
              }
              inspector={selectedChannel === null ? null
                : <ChannelInspector key={selectedChannel.channelId} channel={selectedChannel} onAuthenticationRequired={onAuthenticationRequired} onReloadOverview={loadOverview} onClose={closeChannel} />}
              onCloseInspector={closeFloating}
            />
          </section>
        </Tabs.Panel>
        <Tabs.Panel className="platform-tabs__panel" value="audit">
          <PlatformAudit auditState={audit} channels={overview.data ?? []} onLoadMore={() => { void loadMoreAudit(); }} onRetry={() => { void loadAudit(); }} loadingMore={auditLoadingMore} />
        </Tabs.Panel>
      </Tabs>
      <ChannelRelease opened={channelReleaseOpen} onReloadOverview={loadOverview} onAuthenticationRequired={onAuthenticationRequired} onClose={closeChannelRelease} />
    </section>
  );
};
